import { ChangeDetectionStrategy, Component, HostListener, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { Translation } from './core/i18n/translation.service';
import { confirmDialogPt } from './shared/services/action-confirmation.service';
import { StructuredDataService } from './core/services/structured-data.service';
import { ThemeModeService } from './core/theme/theme-mode.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastModule, ConfirmDialogModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private closePt: { label: string; pt: object } | null = null;

  /** The confirm dialogs' close (X) name, kept as one object per label so the dialogs' `pt` input is stable. */
  protected dialogPt(label: string): object {
    if (this.closePt?.label !== label) this.closePt = { label, pt: confirmDialogPt(label) };
    return this.closePt.pt;
  }

  constructor() {
    inject(ThemeModeService);
    inject(StructuredDataService).setOrganization();
    const translation = inject(Translation);
    // Note: the current language's translations are already guaranteed to be
    // loaded by this point via the `provideAppInitializer` in app.config.ts
    // (which resolves before Angular considers the app initialized/stable).
    // This call is still needed, though: it applies text direction (RTL/LTR)
    // and the PrimeNG locale, and persists the resolved language to storage.
    // `getCurrentLang()` is the single source of truth for language
    // precedence (URL `?lang=` override, then stored preference, then
    // default) — using it here rather than re-deriving it keeps this in sync
    // with what `preloadCurrentLanguage()` already resolved and loaded, so
    // this call only re-fetches when they'd actually differ.
    translation.setLanguage(translation.getCurrentLang(), { reloadPage: false });
  }

  @HostListener('document:wheel', ['$event'])
  protected preventFocusedNumberWheelChange(event: WheelEvent): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'number') {
      return;
    }

    if (document.activeElement === target) {
      target.blur();
    }
  }
}
